import { useCallback, useEffect, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { IOnOffSwitchConfig } from "app/components/React/WidgetContainer/types";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";

interface UseMonthToDateToggleOptions {
    /**
     * The event sub name for tracking
     */
    eventSubName: string;
    /**
     * The query parameter name for the MTD toggle state
     */
    mtdParam: string;
    /**
     * When true, onOffSwitchConfig is undefined
     */
    enabled?: boolean;
}

// A registry of all MTD params to track their state
// We need a central registry to avoid race conditions when updating params one by one
// This way we can update all params at once
const registry = new Set<string>();

export const useMonthToDateToggle = ({ eventSubName, mtdParam, enabled = true }: UseMonthToDateToggleOptions) => {
    const { params } = useCurrentStateAndParams();
    const t = useTranslation();
    const isMonthToDateActive = params[mtdParam] === "on";
    const isMonthToDateSupported = DurationService.isMonthToDateSupportedForDuration(params.duration);

    useEffect(() => {
        // Register MTD param
        if (!registry.has(mtdParam)) {
            registry.add(mtdParam);
        }
        // Unregister MTD param on unmount
        return () => {
            registry.delete(mtdParam);
        };
    }, [params, mtdParam]);

    // When MTD changes to disabled (not "off"), we need to update router state too
    // The problem is that when we change params one by one the last call cancelled the previous updateParam calls
    // So we need to update all params at once
    useEffect(() => {
        if (isMonthToDateActive && (!isMonthToDateSupported || !enabled)) {
            const params = Object.fromEntries(Array.from(registry).map((key) => [key, "off"]));
            void proNavigator.updateParams(params);
        }
    }, [isMonthToDateActive, isMonthToDateSupported, enabled, mtdParam]);

    const handleMonthToDateToggle = useCallback(() => {
        const isOn = !isMonthToDateActive;
        void proNavigator.updateParams({ [mtdParam]: isOn ? "on" : "off" });

        allTrackers.dispatchTrackEvent("Toggle", isOn ? "on" : "off", `MTD/${eventSubName}`);
    }, [isMonthToDateActive, eventSubName, mtdParam]);

    const onOffSwitchConfig: IOnOffSwitchConfig[] = useMemo(() => {
        if (enabled) {
            return [
                {
                    onClick: handleMonthToDateToggle,
                    isSelected: isMonthToDateActive,
                    isDisabled: !isMonthToDateSupported,
                    onLabel: t("demand_analysis.common.month_to_date.label"),
                    className: "month-to-date-switch",
                    tooltip: !isMonthToDateSupported
                        ? { content: t("demand_analysis.common.month_to_date.tooltip.text") }
                        : undefined,
                },
            ];
        }

        return [];
    }, [enabled, isMonthToDateActive, isMonthToDateSupported, handleMonthToDateToggle, t]);

    return {
        isMonthToDateActive,
        isMonthToDateSupported,
        handleMonthToDateToggle,
        onOffSwitchConfig,
    };
};
